import { useEffect, useRef, useState } from "react";
import { IntegrationSetupStepType, OAuth2Auth } from "../../../config/types";
import { checkOAuthCompletion, initiateOAuth, openOAuthAuthorizeURL } from "../../../utils/fetchUtils";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import LoadingScreen from "../../interstitials/LoadingScreen";
import ErrorScreen from "../error-remediation/ErrorScreen";
import ConnectorSetupStepBase from "./ConnectorSetupStepBase";
import { setAgentHandlerLinkCSRFOauthCookie } from "./Helpers";
import use3PCookieDisabled from "./hooks/use3PCookieDisabled";
import useIntegrationSetupContext from "./useIntegrationSetupContext";
import IntegrationSetupContext from "./IntegrationSetupContext";

const Type = IntegrationSetupStepType.STEP_TYPE_OAUTH as const;

type Props = {
  oauth2Auth: OAuth2Auth;
  initialOAuthData?: { authorizeURL: string; key: string };
};

const ConnectorSetupOAuth = ({
  oauth2Auth,
  initialOAuthData,
}: Props) => {
  const { isLinkOpen, onExit, translate, linkToken, allowNavigateToPreviousEntryInHistory } = useAgentHandlerLinkModalContext();
  const integrationSetupContext = useIntegrationSetupContext();
  const {
    connector,
    registeredUser,
    apiBaseURL,
    getFormDataProperty,
    navigateToNextStep,
    shouldAutomaticallyExitOnOAuthOpen,
    shouldBlockOauthOnCookies,
    navigateToPreviousStep,
    registeredUserSecrets,
  } = integrationSetupContext;
  
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const windowRef = useRef<Window>();

  let customDomain = getFormDataProperty("customDomain");

  const [oAuthData, setOAuthData] = useState<{ authorizeURL: string; key: string } | undefined>(
    initialOAuthData,
  );
  const [wasPopupOpened, setWasPopupOpened] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isErrorAuthorizing, setIsErrorAuthorizing] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const shouldShowOpenPopupScreen =
  !isErrorAuthorizing && (!wasPopupOpened || (wasPopupOpened && !isPopupOpen));

  const hasThirdPartyCookiesDisabled = use3PCookieDisabled();

  // If parent passed a pre-fetched OAuth response, set the CSRF cookie once
  // on mount so the popup-based callback can validate state.
  useEffect(() => {
    if (initialOAuthData) {
      setAgentHandlerLinkCSRFOauthCookie(initialOAuthData.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    if (isLinkOpen && !oAuthData) {
      initiateOAuth({
        apiBaseURL,
        registeredUserID: registeredUser.id,
        connectorID: connector.id,
        authOptionName: oauth2Auth.name,
        linkToken: linkToken,
        registeredUserSecrets: registeredUserSecrets, 
        onResponse: (response: { oauth_authorize_url: string; oauth_key: string }) => {
          setAgentHandlerLinkCSRFOauthCookie(response.oauth_key);
          setOAuthData({ authorizeURL: response.oauth_authorize_url, key: response.oauth_key });
        },
        onError: (_errorMessage?: string) => {
          setIsErrorAuthorizing(true);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkOpen, oAuthData]);

  const openPopup = () => {
    
    if (!oAuthData || isPopupOpen) {
      return;
    }
    
    setWasPopupOpened(true);
    setIsPopupOpen(true);
    setPopupBlocked(false);
    let countSinceClosed = 0;

    if (shouldAutomaticallyExitOnOAuthOpen) {
      openOAuthAuthorizeURL(oAuthData?.authorizeURL, customDomain, "_blank");
      onExit();
    } else {
      windowRef.current = openOAuthAuthorizeURL(oAuthData?.authorizeURL, customDomain) || undefined;
    }

    const interval = setInterval(() => {
      checkOAuthCompletion({
        apiBaseURL,
        OAuthKey: oAuthData?.key,
        onResponse: (response: { is_complete: boolean }) => {
          if (response.is_complete) {
            windowRef.current?.close();
            windowRef.current = undefined;
            setIsAuthorized(true);
          } else {
            if (windowRef.current?.closed) {
              countSinceClosed += 1;
              
              if (countSinceClosed > 1) {
                setIsPopupOpen(false);
                setIsErrorAuthorizing(true);
              }
            }
          }
        },
      });
    }, 2000);
    
    intervalRef.current = interval;

    // check ad blocker
    setTimeout(() => {
      if (windowRef.current?.closed) {
        setPopupBlocked(true);
        setIsPopupOpen(false);
        setIsErrorAuthorizing(true);
      }
    }, 300); // if window is closed in first 300ms we assume it was blocked via popup
  };

  useEffect(() => {
    // Automatically proceed to the next step when complete
    if (isAuthorized) {
      navigateToNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  useEffect(
    // Return a cleanup function that will be called when unmounting or before effect re-runs
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (windowRef.current) {
        windowRef.current.close();
      }
    },
    [oAuthData]
  );

  if (shouldShowOpenPopupScreen) {
    return (
      <IntegrationSetupContext.Provider
        value={{
          ...integrationSetupContext,
          navigateToNextStep: openPopup,
          hasThirdPartyCookiesDisabled: shouldBlockOauthOnCookies && hasThirdPartyCookiesDisabled === true,
        }}
      >
        <ConnectorSetupStepBase
          currentStepType={Type}
          description={oauth2Auth.oauth_notice ?? ""}
          connector={connector}
          onExit={onExit}
          navigateToPreviousStep={allowNavigateToPreviousEntryInHistory ? navigateToPreviousStep : undefined}
          navigateToNextStep={openPopup}
          header={translate("website-authorization-title", `Log in to ${connector.name}`)}
          subHeader={
            translate(
              "website-authorization-subtitle",
              `You will be prompted to log in and connect your account`
            )
          }
          isStepComplete={!!oAuthData}
        />
      </IntegrationSetupContext.Provider>
    );
  } else {
    return !isErrorAuthorizing ? (
      <LoadingScreen
        customText={translate("await-authorization", "Awaiting authorization...")}
        onClickBottomAction={() => {
          if (windowRef && windowRef.current) {
            windowRef.current.close();
          }
          if (intervalRef && intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          setIsPopupOpen(false);
          setWasPopupOpened(true);
        }}
        buttonText={translate("cancel-go-back", "Cancel and go back")}
        connector={connector}
      />
    ) : (
      <ErrorScreen
        customText={
          popupBlocked ? (
            <div>
              {translate("attempt-unsuccessful", "Authorization attempt unsuccessful")}
              <br /> <br />
              {translate(
                "popup-blocked-text",
                "Third party software, such as ad-blocking browser extensions, may be interfering. Please check your browser settings and disable any blocking extensions."
              )}
            </div>
          ) : (
            translate(
              "auth-attempt-unsuccessful-generic",
              "Authorization attempt unsuccessful, please try again"
            )
          )
        }
        customButtonText={translate("try-again", "Try again")}
        onClickBottomAction={() => {
          setOAuthData(undefined);
          setIsErrorAuthorizing(false);
        }}
        onExit={() => {
          onExit();
        }}
      />
    );
  }
};

ConnectorSetupOAuth.Type = Type;

export default ConnectorSetupOAuth;
